import CustomHTMLElement from '@onpace/onspace-core/components/html_element'
import { OnspaceDialogSelect } from '@onpace/onspace-core/elements/dialog'

/// An element which allows managing priorities via drag and drop.
export default class OnspacePriorities extends CustomHTMLElement {
  /// Sets up the priorities element.
  ///
  /// This locates and configures the elements.
  runConstructor() {
    super.runConstructor()

    this.formElement = this.querySelector('form')
    this.configureTable()
    this.configureSelectionDialog()
  }

  ////////// Table

  configureTable() {
    this.tableElement = this.querySelector('table')

    this.prioritisedTableBody = this.tableElement.querySelector('tbody[data-prioritised]')
    this.prioritisedTableBody.addEventListener('dragover', this.tablePrioritisedDraggedOver.bind(this))
    this.prioritisedTableRows.forEach((row) => this.configureTableRow(row))

    this.deprioritisedTableBody = this.tableElement.querySelector('tbody[data-deprioritised]')
    if (this.deprioritisedTableBody) {
      this.deprioritisedTableBody.addEventListener('dragover', this.tableDeprioritisedDraggedOver.bind(this))
    }
  }

  configureTableRow(row) {
    row.draggable = true
    row.addEventListener('dragstart', this.rowDragStarted.bind(this))
    row.addEventListener('dragend', this.rowDragEnded.bind(this))

    row.priorityInput = row.querySelector('input[type=hidden]')
    row.priorityElement = row.querySelector('td.onspace-table__priority div.formatted-field__value')
    row.modelId = row.priorityInput.name.replace(/^priorities\[(\d+)\]$/, '$1')
  }

  /// Retrieves the prioritised table rows from the DOM.
  get prioritisedTableRows() {
    return this.prioritisedTableBody.querySelectorAll('tr')
  }

  /// Retrieves the deprioritised table rows from the DOM.
  get deprioritisedTableRows() {
    if (!this.deprioritisedTableBody) { return [] }

    return this.deprioritisedTableBody.querySelectorAll('tr:not(.onspace-priorities__deprioritised)')
  }

  /// Updates the table rows to reflect the current priorities.
  reflowPriorities() {
    this.prioritisedTableRows.forEach((row, index) => {
      const newPriority = (index + 1).toString()

      row.priorityInput.value = newPriority
      row.priorityElement.innerText = newPriority
    })
    this.deprioritisedTableRows.forEach((row, _index) => {
      row.priorityInput.value = ''
      row.priorityElement.innerHTML = '<span class="formatted-field__blank">—</span>'
    })
  }

  ///// Events

  /// Responds to a drag beginning on a table row.
  rowDragStarted(event) {
    this.dragChanged = false
    this.draggedRow = event.target
    this.dragControls = Array.from(this.prioritisedTableRows)
    this.dragIndex = this.dragControls.indexOf(this.draggedRow)
    if (this.dragIndex < 0) { this.dragIndex = null }

    const offset = document.documentElement.scrollTop - document.documentElement.clientTop
    this.dragCenters = this.dragControls.map((i) => {
      const rect = i.getBoundingClientRect()
      return rect.top + (rect.height / 2) + offset
    })

    this.classList.add('onspace-priorities--dragging')
    setTimeout(() => event.target.classList.add('onspace-table__row--dragging'))
  }

  /// Responds to a drag ending on a table row.
  rowDragEnded(event) {
    this.classList.remove('onspace-priorities--dragging')
    event.target.classList.remove('onspace-table__row--dragging')

    if (this.dragChanged) {
      this.reflowPriorities()

      this.formElement.onspaceHasChanges = true
      this.formElement.triggerEvent('onspace:form:change')
    }

    this.dragChanged = false
    this.draggedRow = null
    this.dragControls = null
    this.dragCenters = null
    this.dragIndex = null
  }

  /// Responds to a dragged element updating over the table's prioritised body.
  ///
  /// This calculates the position of the control relative to the other controls.
  tablePrioritisedDraggedOver(event) {
    if (!this.draggedRow) { return }
    event.preventDefault()

    let i = -1
    do {
      i += 1
    } while (i < this.dragControls.length && event.pageY > this.dragCenters[i])

    if (i !== this.dragIndex) {
      this.prioritisedTableBody.insertBefore(this.draggedRow, this.dragControls[i])
      this.dragIndex = i
      this.dragChanged = true
    }
  }

  /// Responds to a dragged element updating over the table's deprioritised body.
  ///
  /// This moves the dragged row to have a blank priority value.
  tableDeprioritisedDraggedOver(event) {
    if (!this.draggedRow) { return }
    event.preventDefault()

    this.deprioritisedTableBody.appendChild(this.draggedRow)
    this.dragIndex = null
  }

  ////////// Selection

  /// Configures selecting items via a dialog.
  configureSelectionDialog() {
    const selectButton = this.querySelector('[data-deprioritised-select]')
    if (!selectButton) { return }
    selectButton.addEventListener('click', this.presentSelectionDialog.bind(this))

    this.selectionUrl = selectButton.getAttribute('data-deprioritised-select')
  }

  /// Creates and presents a new selection dialog instance.
  presentSelectionDialog() {
    const selectedValues = []
    this.prioritisedTableRows.forEach((row) => selectedValues.push(row.modelId))
    this.deprioritisedTableRows.forEach((row) => selectedValues.push(row.modelId))

    this.selectionDialog = new OnspaceDialogSelect({ selectedValues })
    this.selectionDialog.addEventListener('onspace:dialog:close', this.dialogClosed.bind(this))
    this.selectionDialog.addEventListener('onspace:dialog:selected', this.selectionDialogValueSelected.bind(this))
    this.selectionDialog.addEventListener('onspace:dialog:deselected', this.selectionDialogValueDeselected.bind(this))
    this.selectionDialog.loadUrl(this.selectionUrl)

    document.body.appendChild(this.selectionDialog)
  }

  ///// Events

  /// Responds to close events on the selection dialog.
  dialogClosed(_event) {
    this.selectionDialog = null
  }

  /// Responds to selection events on the selection dialog.
  selectionDialogValueSelected(event) {
    let row = Array.from(this.deprioritisedTableRows).find((r) => r.modelId === event.detail.value)
    if (!row) {
      row = document.createElement('tr')
      row.innerHTML = event.detail.item.innerHTML
      row.querySelector('.onspace-table__selection').remove()

      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `priorities[${event.detail.value}]`

      const priorityElement = row.querySelector('.onspace-table__priority')
      priorityElement.insertBefore(input, priorityElement.firstChild)

      this.configureTableRow(row)
    }

    this.prioritisedTableBody.appendChild(row)
    this.reflowPriorities()
  }

  /// Responds to deselection events on the selection dialog.
  selectionDialogValueDeselected(event) {
    const row = Array.from(this.prioritisedTableRows).find((r) => r.modelId === event.detail.value)
    if (!row) { return }

    this.deprioritisedTableBody.appendChild(row)
    this.reflowPriorities()
  }
}

window.customElements.define('onspace-priorities', OnspacePriorities)
